import { serverConfig } from '@/config/server';
import type {
  Organization,
  OrganizationFilters,
  OrganizationStats,
  OrganizationWithFacilities,
} from '@/types';

class OrganizationService {
  private readonly baseUrl: string;

  public constructor() {
    this.baseUrl = serverConfig.baseUrl;
  }

  public async fetchOrganizations(
    filters?: OrganizationFilters
  ): Promise<readonly Organization[]> {
    const params = new URLSearchParams();

    if (filters?.min_confidence !== undefined) {
      params.set('min_confidence', filters.min_confidence.toString());
    }

    if (filters?.min_facility_count !== undefined) {
      params.set('min_facility_count', filters.min_facility_count.toString());
    }

    if (filters?.sort_by) {
      params.set('sort_by', filters.sort_by);
    }

    if (filters?.sort_order) {
      params.set('sort_order', filters.sort_order);
    }

    if (filters?.limit !== undefined) {
      params.set('limit', filters.limit.toString());
    }

    if (filters?.offset !== undefined) {
      params.set('offset', filters.offset.toString());
    }

    const queryString = params.toString();
    const url = queryString
      ? `${this.baseUrl}/organizations?${queryString}`
      : `${this.baseUrl}/organizations`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch organizations: ${response.statusText}`);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : data.organizations || [];
  }

  public async fetchOrganizationById(
    id: number
  ): Promise<OrganizationWithFacilities> {
    const response = await fetch(`${this.baseUrl}/organizations/${id}`);

    if (!response.ok) {
      throw new Error(`Failed to fetch organization: ${response.statusText}`);
    }

    return response.json();
  }

  public async fetchStats(): Promise<OrganizationStats> {
    const response = await fetch(`${this.baseUrl}/organizations/stats`);

    if (!response.ok) {
      // Return defaults if endpoint not implemented yet
      if (response.status === 404) {
        return {
          total_organizations: 0,
          assigned_facilities: 0,
          total_facilities: 0,
          coverage_percentage: 0,
        };
      }
      throw new Error(
        `Failed to fetch organization stats: ${response.statusText}`
      );
    }

    return response.json();
  }
}

export const organizationService = new OrganizationService();
